import { ICrossList } from 'features/cross/types';
import { formatTransactionAmount } from 'features/transaction-details/helpers';

export type EticketCrossCheckSeverity = 'ok' | 'info' | 'warning' | 'error';

export interface EticketCrossAuthGroup {
	authorizationId: number;
	transactionId: number;
	authorizationCode: string;
	cardLastFourDigit: string;
	coinCode: string;
	validatingCarrierName: string;
	netAmount: number;
	events: ICrossList[];
	isCurrentAuthorization: boolean;
}

export interface EticketCrossCheckResult {
	severity: EticketCrossCheckSeverity;
	title: string;
	message: string;
	canProceed: boolean;
	groups: EticketCrossAuthGroup[];
	activeElsewhere: EticketCrossAuthGroup[];
	activeOnCurrent: EticketCrossAuthGroup | null;
}

const buildAuthGroup = (
	authorizationId: number,
	events: ICrossList[],
	currentAuthorizationId: number,
): EticketCrossAuthGroup => {
	const sorted = [...events].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
	const netAmount = Math.max(
		0,
		sorted.reduce((sum, e) => sum + e.amount, 0),
	);
	const last = sorted[sorted.length - 1];

	return {
		authorizationId,
		transactionId: last.transactionId,
		authorizationCode: String(last.authorizationCode),
		cardLastFourDigit: String(last.cardLastFourDigit),
		coinCode: last.coinCode,
		validatingCarrierName: last.validatingCarrierName,
		netAmount,
		events: sorted,
		isCurrentAuthorization: authorizationId === currentAuthorizationId,
	};
};

const formatGroupAmount = (group: EticketCrossAuthGroup): string =>
	formatTransactionAmount(group.netAmount, group.coinCode);

export const analyzeEticketCrossHistory = (
	rows: ICrossList[],
	currentAuthorizationId: number,
): EticketCrossCheckResult => {
	const byAuth = new Map<number, ICrossList[]>();

	for (const row of rows) {
		const list = byAuth.get(row.authorizationId) ?? [];
		list.push(row);
		byAuth.set(row.authorizationId, list);
	}

	const groups: EticketCrossAuthGroup[] = [];
	let activeOnCurrent: EticketCrossAuthGroup | null = null;
	const activeElsewhere: EticketCrossAuthGroup[] = [];

	for (const [authorizationId, events] of byAuth) {
		const group = buildAuthGroup(authorizationId, events, currentAuthorizationId);
		groups.push(group);
		if (group.netAmount > 0) {
			if (group.isCurrentAuthorization) {
				activeOnCurrent = group;
			} else {
				activeElsewhere.push(group);
			}
		}
	}

	groups.sort(
		(a, b) =>
			new Date(b.events[b.events.length - 1].createdAt).getTime() -
			new Date(a.events[a.events.length - 1].createdAt).getTime(),
	);

	if (activeOnCurrent) {
		return {
			severity: 'error',
			title: 'Boleto ya cruzado en esta autorización',
			message: `Este e-ticket ya tiene un cruce vigente de ${formatGroupAmount(activeOnCurrent)} en la autorización #${
				activeOnCurrent.authorizationId
			}. Desvinculalo antes de volver a cruzar.`,
			canProceed: false,
			groups,
			activeElsewhere,
			activeOnCurrent,
		};
	}

	if (activeElsewhere.length > 0) {
		const first = activeElsewhere[0];
		const others = activeElsewhere.length > 1 ? ` y ${activeElsewhere.length - 1} autorización(es) más` : '';
		return {
			severity: 'error',
			title: 'Boleto cruzado en otra autorización',
			message: `Este e-ticket está cruzado en la transacción #${first.transactionId}, autorización #${
				first.authorizationId
			} (tarjeta ****${first.cardLastFourDigit}), por ${formatGroupAmount(
				first,
			)}${others}. Desvinculalo allí antes de cruzarlo aquí.`,
			canProceed: false,
			groups,
			activeElsewhere,
			activeOnCurrent: null,
		};
	}

	if (rows.length > 0) {
		return {
			severity: 'info',
			title: 'Historial de cruces',
			message:
				'Este boleto tuvo cruces anteriores que ya fueron desvinculados. Podés cruzarlo nuevamente en esta autorización.',
			canProceed: true,
			groups,
			activeElsewhere: [],
			activeOnCurrent: null,
		};
	}

	return {
		severity: 'ok',
		title: 'Sin cruces previos',
		message: 'No encontramos cruces registrados con este e-ticket.',
		canProceed: true,
		groups: [],
		activeElsewhere: [],
		activeOnCurrent: null,
	};
};
